import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import {
  GanttValidationResult,
  ItemChangeRecord,
  Pipe,
  PipeStatus,
  PipeType,
  ProcorePdfSize,
  Project,
  ProjectNotificationPolicy,
  projectsApi,
  SmartsheetRemoteColumn,
  SmartsheetSheetSummary,
  syncApi,
  SyncItemMapping,
  SyncSessionSummaryResponse,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import { Link, useParams } from '../../router/Router';
import {
  Badge,
  BadgeVariant,
  formatDateTime,
  formatRelativeTime,
} from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconPlus, IconSettings, IconSync, IconTrash } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import { usePaginatedList } from '../../hooks/usePagination';

const STANDARD_FIELDS_BY_PIPE_TYPE: Record<PipeType, Array<{ key: string; label: string; required?: boolean }>> = {
  tasks: [
    { key: 'task_name', label: 'Task / Activity Name', required: true },
    { key: 'start_date', label: 'Start Date', required: true },
    { key: 'end_date', label: 'Finish / End Date', required: true },
    { key: 'duration', label: 'Duration (Days)' },
    { key: 'percent_complete', label: '% Complete' },
    { key: 'predecessors', label: 'Predecessors' },
    { key: 'assigned_to', label: 'Assignee / Resource' },
    { key: 'status', label: 'Task Status' },
  ],
  submittals: [
    { key: 'submittal_number', label: 'Submittal Number', required: true },
    { key: 'title', label: 'Submittal Title', required: true },
    { key: 'status', label: 'Review Status', required: true },
    { key: 'spec_section', label: 'Specification Section' },
    { key: 'responsible_contractor', label: 'Responsible Contractor' },
    { key: 'submit_by_date', label: 'Submit By Date' },
    { key: 'received_date', label: 'Received Date' },
  ],
  rfis: [
    { key: 'rfi_number', label: 'RFI Number', required: true },
    { key: 'subject', label: 'Subject / Question', required: true },
    { key: 'status', label: 'RFI Status', required: true },
    { key: 'assignee', label: 'Assigned Ball-in-Court' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'official_response', label: 'Official Response' },
  ],
  project_stages: [
    { key: 'stage_name', label: 'Project Stage Name', required: true },
    { key: 'category', label: 'Stage Category' },
    { key: 'is_bidding', label: 'Bidding Stage Flag' },
  ],
};

function buildDefaultColumnMap(
  pipeType: PipeType,
  columns: SmartsheetRemoteColumn[],
): Record<string, string> {
  if (!Array.isArray(columns) || columns.length === 0) return {};
  const map: Record<string, string> = {};
  const findColId = (candidates: string[], fallbackPrimary = false): string | undefined => {
    for (const candidate of candidates) {
      const lower = candidate.toLowerCase();
      const exact = columns.find((c) => (c.title || '').trim().toLowerCase() === lower);
      if (exact) return String(exact.id);
    }
    for (const candidate of candidates) {
      const lower = candidate.toLowerCase();
      const partial = columns.find((c) => (c.title || '').trim().toLowerCase().includes(lower));
      if (partial) return String(partial.id);
    }
    if (fallbackPrimary) {
      const primary = columns.find((c) => c.primary);
      if (primary) return String(primary.id);
    }
    return undefined;
  };

  if (pipeType === 'tasks') {
    const taskName = findColId(['task name', 'task', 'activity name', 'name'], true);
    const startDate = findColId(['start date', 'start']);
    const endDate = findColId(['end date', 'finish date', 'finish', 'end']);
    const duration = findColId(['duration']);
    const percentComplete = findColId(['% complete', 'percent complete', 'complete']);
    const predecessors = findColId(['predecessors', 'predecessor']);
    const assignedTo = findColId(['assigned to', 'assignee', 'resource']);
    const status = findColId(['status']);

    if (taskName) map.task_name = taskName;
    if (startDate) map.start_date = startDate;
    if (endDate) map.end_date = endDate;
    if (duration) map.duration = duration;
    if (percentComplete) map.percent_complete = percentComplete;
    if (predecessors) map.predecessors = predecessors;
    if (assignedTo) map.assigned_to = assignedTo;
    if (status) map.status = status;
  }
  return map;
}

function pipeStatusBadge(status: PipeStatus): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'connected':
      return { variant: 'success', label: 'Connected' };
    case 'sync_failed':
      return { variant: 'danger', label: 'Sync Failed' };
    case 'limit_exceeded':
      return { variant: 'warning', label: 'Limit Exceeded' };
    case 'disabled':
      return { variant: 'warning', label: 'Disabled' };
    default:
      return { variant: 'neutral', label: 'Not Configured' };
  }
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { pushToast } = useToast();

  const [project, setProject] = useState<Project | null>(null);
  const [pipes, setPipes] = useState<Pipe[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busyPipeId, setBusyPipeId] = useState<string | null>(null);

  // Create Pipe Wizard State
  const [createPipeOpen, setCreatePipeOpen] = useState<boolean>(false);
  const [pipeName, setPipeName] = useState<string>('');
  const [pipeType, setPipeType] = useState<PipeType>('tasks');
  const [useScheduleChart, setUseScheduleChart] = useState<boolean>(true);
  const [availableSheets, setAvailableSheets] = useState<SmartsheetSheetSummary[]>([]);
  const [loadingSheets, setLoadingSheets] = useState<boolean>(false);
  const [sheetSelectionMode, setSheetSelectionMode] = useState<'existing' | 'create'>('existing');
  const [selectedSheetId, setSelectedSheetId] = useState<string>('');
  const [newSheetName, setNewSheetName] = useState<string>('');
  const [creatingPipe, setCreatingPipe] = useState<boolean>(false);

  // Column Matching Modal State
  const [mappingPipe, setMappingPipe] = useState<Pipe | null>(null);
  const [remoteColumns, setRemoteColumns] = useState<SmartsheetRemoteColumn[]>([]);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [columnMapDraft, setColumnMapDraft] = useState<Record<string, string>>({});
  const [savingColumns, setSavingColumns] = useState<boolean>(false);

  // Pipe Settings & Diagnostics Modal State
  const [editingPipe, setEditingPipe] = useState<Pipe | null>(null);
  const [editPipeName, setEditPipeName] = useState<string>('');
  const [editUseScheduleChart, setEditUseScheduleChart] = useState<boolean>(false);
  const [editColorsCoding, setEditColorsCoding] = useState<boolean>(false);
  const [editSummaryTasks, setEditSummaryTasks] = useState<boolean>(false);
  const [editPdfSize, setEditPdfSize] = useState<ProcorePdfSize>('A2');
  const [savingPipeSettings, setSavingPipeSettings] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<GanttValidationResult | null>(null);

  // Sync History & Item Change Logs Modal State
  const [historyPipe, setHistoryPipe] = useState<Pipe | null>(null);
  const [sessions, setSessions] = useState<SyncSessionSummaryResponse[]>([]);
  const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sessionChanges, setSessionChanges] = useState<ItemChangeRecord[]>([]);
  const [loadingChanges, setLoadingChanges] = useState<boolean>(false);
  const [syncedItems, setSyncedItems] = useState<SyncItemMapping[]>([]);

  // Project Settings Modal State
  const [projectSettingsOpen, setProjectSettingsOpen] = useState<boolean>(false);
  const [workingDaysDraft, setWorkingDaysDraft] = useState<number[]>([1, 2, 3, 4, 5]);
  const [holidaysDraft, setHolidaysDraft] = useState<string>('');
  const [allowNestedDraft, setAllowNestedDraft] = useState<boolean>(true);
  const [updateTasksStatusDraft, setUpdateTasksStatusDraft] = useState<boolean>(true);
  const [notificationPolicyDraft, setNotificationPolicyDraft] =
    useState<ProjectNotificationPolicy>('send_once_per_day');
  const [additionalEmailsDraft, setAdditionalEmailsDraft] = useState<string>('');
  const [separateInvoiceDraft, setSeparateInvoiceDraft] = useState<boolean>(false);
  const [savingProjectSettings, setSavingProjectSettings] = useState<boolean>(false);

  const loadWorkspace = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [proj, pipeList] = await Promise.all([
        projectsApi.getProject(projectId),
        projectsApi.listProjectPipes(projectId),
      ]);
      setProject(proj);
      setPipes(Array.isArray(pipeList) ? pipeList : []);
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to load project workspace',
        description: err instanceof Error ? err.message : 'Project not found',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, pushToast]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  // --------------------------------------------------------------------------
  // Procore Webhook Handlers
  // --------------------------------------------------------------------------
  const handleToggleProcoreWebhook = async () => {
    if (!project) return;
    try {
      if (project.webhook?.status === 'active') {
        const updated = await projectsApi.removeProcoreWebhook(project.id);
        setProject(updated);
        pushToast({
          tone: 'info',
          title: 'Procore project webhook removed',
        });
      } else {
        const updated = await projectsApi.createProcoreWebhook(project.id);
        const withTriggers = await projectsApi.addProcoreWebhookTriggers(project.id, ['Tasks']);
        setProject(withTriggers || updated);
        pushToast({
          tone: 'success',
          title: 'Procore project webhook installed',
          description: 'Subscribed to Schedule Tasks events.',
        });
      }
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Webhook operation failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  // --------------------------------------------------------------------------
  // Project Settings Handlers
  // --------------------------------------------------------------------------
  const openProjectSettings = () => {
    if (!project) return;
    setWorkingDaysDraft(project.settings?.working_days || [1, 2, 3, 4, 5]);
    setHolidaysDraft((project.settings?.holidays || []).join(', '));
    setAllowNestedDraft(project.settings?.allow_nested_tasks ?? true);
    setUpdateTasksStatusDraft(project.settings?.update_tasks_status ?? true);
    setNotificationPolicyDraft(project.settings?.notification_policy || 'send_once_per_day');
    setAdditionalEmailsDraft((project.settings?.additional_emails || []).join(', '));
    setSeparateInvoiceDraft(project.is_separate_invoice);
    setProjectSettingsOpen(true);
  };

  const handleSaveProjectSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (!project) return;
    setSavingProjectSettings(true);
    try {
      const updated = await projectsApi.updateProjectSettings(project.id, {
        working_days: workingDaysDraft,
        holidays: holidaysDraft
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        allow_nested_tasks: allowNestedDraft,
        update_tasks_status: updateTasksStatusDraft,
        notification_policy: notificationPolicyDraft,
        additional_emails: additionalEmailsDraft
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      if (separateInvoiceDraft !== project.is_separate_invoice) {
        const invUpdated = await projectsApi.setSeparateInvoiceState(
          project.id,
          separateInvoiceDraft,
        );
        setProject(invUpdated);
      } else {
        setProject(updated);
      }
      setProjectSettingsOpen(false);
      pushToast({
        tone: 'success',
        title: 'Project settings updated',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to update project settings',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingProjectSettings(false);
    }
  };

  // --------------------------------------------------------------------------
  // Pipe Creation Wizard Handlers
  // --------------------------------------------------------------------------
  const openCreatePipeModal = async () => {
    const defaultSheetName = `${project?.name || 'Project'} - Schedule`;
    setCreatePipeOpen(true);
    setPipeName(`${project?.name || 'Project'} — Master Schedule Sync`);
    setPipeType('tasks');
    setNewSheetName(defaultSheetName);
    setSelectedSheetId('');
    setLoadingSheets(true);
    try {
      const sheets = await syncApi.listSmartsheetSheets();
      const validSheets = Array.isArray(sheets)
        ? sheets.map((s) => ({
            ...s,
            id: Number(
              (s as SmartsheetSheetSummary & { sheet_id?: number }).id ??
                (s as SmartsheetSheetSummary & { sheet_id?: number }).sheet_id ??
                0,
            ),
          }))
        : [];
      setAvailableSheets(validSheets);
      if (validSheets.length > 0 && validSheets[0].id > 0) {
        setSheetSelectionMode('existing');
        setSelectedSheetId(String(validSheets[0].id));
      } else {
        setSheetSelectionMode('create');
      }
    } catch {
      setAvailableSheets([]);
      setSheetSelectionMode('create');
    } finally {
      setLoadingSheets(false);
    }
  };

  const handleCreatePipe = async (e: FormEvent) => {
    e.preventDefault();
    if (!project || !pipeName.trim()) return;
    setCreatingPipe(true);
    try {
      let smSheetId: number | undefined;
      let smSheetName: string | undefined;
      let smPermalink: string | undefined;
      let initialColumns: SmartsheetRemoteColumn[] = [];

      if (sheetSelectionMode === 'create') {
        const targetSheetName = newSheetName.trim() || `${project.name} - Schedule`;
        const createdSheet = await syncApi.createSmartsheetSheet(targetSheetName);
        const resolvedCreatedId = Number(
          (createdSheet as { id?: number; sheet_id?: number }).id ??
            (createdSheet as { id?: number; sheet_id?: number }).sheet_id ??
            0,
        );
        smSheetId = resolvedCreatedId > 0 ? resolvedCreatedId : undefined;
        smSheetName = createdSheet.name || targetSheetName;
        smPermalink = createdSheet.permalink || undefined;
        if (Array.isArray(createdSheet.columns)) {
          initialColumns = createdSheet.columns;
        }
      } else if (selectedSheetId) {
        smSheetId = Number(selectedSheetId);
        const found = availableSheets.find((s) => String(s.id) === selectedSheetId);
        smSheetName = found?.name || `Smartsheet #${selectedSheetId}`;
        smPermalink = found?.permalink || undefined;
        if (smSheetId > 0) {
          try {
            const remoteSheet = await syncApi.getSmartsheetSheet(smSheetId);
            if (Array.isArray(remoteSheet.columns)) {
              initialColumns = remoteSheet.columns;
            }
          } catch {
            // Non-fatal: user can map columns manually afterwards
          }
        }
      }

      let created = await projectsApi.createPipe({
        project_id: project.id,
        name: pipeName.trim(),
        pipe_type: pipeType,
        connected_to: 'smartsheet',
        use_schedule_chart: useScheduleChart,
        sm_sheet_id: smSheetId,
        sm_sheet_name: smSheetName,
        sm_permalink: smPermalink,
      });

      const defaultMap = buildDefaultColumnMap(pipeType, initialColumns);
      if (Object.keys(defaultMap).length > 0) {
        try {
          created = await projectsApi.saveMatchedColumns({
            pipe_id: created.id,
            connected_to: 'smartsheet',
            column_map: defaultMap,
          });
          created = await projectsApi.enablePipe(created.id);
        } catch {
          // Non-fatal if auto-mapping or auto-enabling fails
        }
      }

      setPipes((prev) => [...prev, created]);
      setCreatePipeOpen(false);
      pushToast({
        tone: 'success',
        title: 'Synchronization pipe created',
        description:
          Object.keys(defaultMap).length > 0
            ? `${created.name} added and default Smartsheet schedule columns mapped.`
            : `${created.name} (${created.pipe_type}) added. Configure column mappings next.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to create pipe',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingPipe(false);
    }
  };

  // --------------------------------------------------------------------------
  // Column Matching / Field Mapping Handlers
  // --------------------------------------------------------------------------
  const openColumnMatching = async (pipe: Pipe) => {
    setMappingPipe(pipe);
    const existingMap = pipe.smartsheet?.column_map || {};

    const initialDraft: Record<string, string> = {};
    for (const [k, v] of Object.entries(existingMap)) {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        initialDraft[k] = String(v);
      }
    }
    setColumnMapDraft(initialDraft);

    if (pipe.smartsheet?.sheet_id) {
      setLoadingColumns(true);
      try {
        const sheet = await syncApi.getSmartsheetSheet(pipe.smartsheet.sheet_id);
        const cols = Array.isArray(sheet.columns) ? sheet.columns : [];
        setRemoteColumns(cols);
        if (Object.keys(initialDraft).length === 0 && cols.length > 0) {
          setColumnMapDraft(buildDefaultColumnMap(pipe.pipe_type, cols));
        }
      } catch {
        setRemoteColumns([]);
      } finally {
        setLoadingColumns(false);
      }
    } else {
      setRemoteColumns([]);
    }
  };

  const handleSaveColumnMatching = async (e: FormEvent) => {
    e.preventDefault();
    if (!mappingPipe) return;
    setSavingColumns(true);
    try {
      const cleanedMap: Record<string, string> = {};
      for (const [k, v] of Object.entries(columnMapDraft)) {
        if (v && v.trim()) {
          cleanedMap[k] = v.trim();
        }
      }
      const updated = await projectsApi.saveMatchedColumns({
        pipe_id: mappingPipe.id,
        connected_to: 'smartsheet',
        column_map: cleanedMap,
      });
      setPipes((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setMappingPipe(null);
      pushToast({
        tone: 'success',
        title: 'Column mappings saved',
        description: `Mapped ${Object.keys(cleanedMap).length} fields for ${updated.name}.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not save column mappings',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingColumns(false);
    }
  };

  // --------------------------------------------------------------------------
  // Pipe Sync, Enable/Disable, Lock Release & Diagnostics
  // --------------------------------------------------------------------------
  const handleTriggerDirectionalSync = async (
    pipe: Pipe,
    direction: 'procore_to_smartsheet' | 'smartsheet_to_procore',
  ) => {
    setBusyPipeId(pipe.id);
    try {
      const res =
        direction === 'procore_to_smartsheet'
          ? await syncApi.triggerProcoreToSmartsheet(pipe.id)
          : await syncApi.triggerSmartsheetToProcore(pipe.id);
      pushToast({
        tone: 'success',
        title: 'Sync job queued (`202 Accepted`)',
        description: `Job ${res.job_id} (${res.direction}) is now running.`,
      });
      await loadWorkspace();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Sync trigger failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setBusyPipeId(null);
    }
  };

  const handleTogglePipeEnabled = async (pipe: Pipe) => {
    setBusyPipeId(pipe.id);
    try {
      const updated =
        pipe.status === 'connected'
          ? await projectsApi.disablePipe(pipe.id, 'Paused by user from Client Portal')
          : await projectsApi.enablePipe(pipe.id);
      setPipes((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      pushToast({
        tone: 'info',
        title: updated.status === 'connected' ? 'Pipe enabled' : 'Pipe disabled',
        description: `${updated.name} is now ${updated.status}.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not update pipe state',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setBusyPipeId(null);
    }
  };

  const handleReleasePipeLock = async (pipe: Pipe) => {
    setBusyPipeId(pipe.id);
    try {
      await syncApi.releasePipeLock(pipe.id);
      pushToast({
        tone: 'success',
        title: 'Synchronization lock released',
        description: `Lock cleared for ${pipe.name}.`,
      });
      await loadWorkspace();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to release lock',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setBusyPipeId(null);
    }
  };

  const handleDeletePipe = async (pipe: Pipe) => {
    setBusyPipeId(pipe.id);
    try {
      await projectsApi.deletePipe(pipe.id);
      setPipes((prev) => prev.filter((p) => p.id !== pipe.id));
      pushToast({
        tone: 'info',
        title: 'Pipe deleted',
        description: `${pipe.name} was removed.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to delete pipe',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setBusyPipeId(null);
    }
  };

  // --------------------------------------------------------------------------
  // Pipe Settings & Diagnostics Modal
  // --------------------------------------------------------------------------
  const openEditPipeSettings = (pipe: Pipe) => {
    setEditingPipe(pipe);
    setEditPipeName(pipe.name);
    setEditUseScheduleChart(pipe.settings?.use_schedule_chart ?? false);
    setEditColorsCoding(pipe.settings?.colors_coding_enabled ?? false);
    setEditSummaryTasks(pipe.settings?.summary_tasks_enabled ?? false);
    setEditPdfSize(pipe.settings?.procore_pdf_size || 'A2');
    setValidationResult(null);
  };

  const handleSavePipeSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingPipe) return;
    setSavingPipeSettings(true);
    try {
      const updated = await projectsApi.updatePipe({
        pipe_id: editingPipe.id,
        name: editPipeName.trim(),
        use_schedule_chart: editUseScheduleChart,
        colors_coding_enabled: editColorsCoding,
        summary_tasks_enabled: editSummaryTasks,
        procore_pdf_size: editPdfSize,
      });
      setPipes((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setEditingPipe(null);
      pushToast({
        tone: 'success',
        title: 'Pipe settings saved',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not save pipe settings',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingPipeSettings(false);
    }
  };

  const handleCleanupDuplicateTasks = async () => {
    if (!editingPipe) return;
    try {
      const res = await syncApi.cleanupDuplicateTasks(editingPipe.id);
      pushToast({
        tone: 'success',
        title: 'Duplicate task cleanup complete',
        description: `Removed ${res.removed_count} duplicate task mappings.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Cleanup failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleRunGanttValidation = async () => {
    try {
      const res = await syncApi.validateGanttTasks([
        {
          id: 'task-1',
          name: 'Site Grading & Foundation',
          start_date: '2026-03-01',
          end_date: '2026-03-15',
          duration_days: 14,
          predecessors: [],
        },
        {
          id: 'task-2',
          name: 'Structural Steel Erection',
          start_date: '2026-03-16',
          end_date: '2026-04-10',
          duration_days: 25,
          predecessors: ['task-1'],
        },
      ]);
      setValidationResult(res);
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Validation request failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  // --------------------------------------------------------------------------
  // Sync History & Item Change Logs Modal
  // --------------------------------------------------------------------------
  const openSyncHistory = async (pipe: Pipe) => {
    setHistoryPipe(pipe);
    setSelectedSessionId(null);
    setSessionChanges([]);
    setLoadingSessions(true);
    try {
      const [sessionList, itemsList] = await Promise.all([
        syncApi.listPipeSyncSessions(pipe.id, 100).catch(() => []),
        syncApi.getPipeSyncedItems(pipe.id).catch(() => []),
      ]);
      setSessions(Array.isArray(sessionList) ? sessionList : []);
      setSyncedItems(Array.isArray(itemsList) ? itemsList : []);
      if (sessionList.length > 0) {
        void handleSelectSyncSession(sessionList[0].id);
      }
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleSelectSyncSession = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setLoadingChanges(true);
    try {
      const changes = await syncApi.getItemChangesBySession(sessionId);
      setSessionChanges(Array.isArray(changes) ? changes : []);
    } catch {
      setSessionChanges([]);
    } finally {
      setLoadingChanges(false);
    }
  };

  const pipesPagination = usePaginatedList(pipes, 10);
  const sessionsPagination = usePaginatedList(sessions, 10);
  const changesPagination = usePaginatedList(sessionChanges, 10);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
        Loading project workspace and synchronization pipes…
      </div>
    );
  }

  if (!project) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center space-y-3">
        <p className="text-base font-semibold text-slate-900">Project not found</p>
        <Link
          to="/projects"
          className="inline-block rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white"
        >
          ← Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Breadcrumb & Project Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/projects" className="hover:text-indigo-600 font-medium">
              Projects
            </Link>
            <span>/</span>
            <span className="font-mono text-slate-700">#{project.procore_project_id}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{project.name}</h1>
            <Badge
              variant={project.status === 'connected' ? 'success' : 'neutral'}
              dot
            >
              {project.status}
            </Badge>
            {project.webhook?.status === 'active' ? (
              <Badge variant="purple" dot>
                Webhook Active ({project.webhook.triggers?.join(', ') || 'Tasks'})
              </Badge>
            ) : (
              <Badge variant="neutral">Webhook Not Installed</Badge>
            )}
          </div>

          <p className="text-xs text-slate-500">
            Procore Project <code className="font-mono">#{project.procore_project_id}</code> ·
            Company <code className="font-mono">#{project.procore_company_id}</code> · Working
            Days: {project.settings?.working_days?.join(', ') || '1, 2, 3, 4, 5'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={handleToggleProcoreWebhook}
            className="rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            {project.webhook?.status === 'active'
              ? 'Remove Procore Webhook'
              : 'Install Procore Webhook'}
          </button>

          <button
            type="button"
            onClick={openProjectSettings}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSettings className="w-4 h-4" />
            <span>Project Settings</span>
          </button>

          <button
            type="button"
            onClick={openCreatePipeModal}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 shadow-xs"
          >
            <IconPlus className="w-4 h-4" />
            <span>New Sync Pipe</span>
          </button>
        </div>
      </div>

      {/* Synchronization Pipes List */}
      <section aria-labelledby="pipes-heading" className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 id="pipes-heading" className="text-lg font-bold text-slate-900 tabular-nums">
              Synchronization Pipes ({pipes.length})
            </h2>
            <p className="text-xs text-slate-500">
              Each pipe synchronizes Procore Schedule &amp; Tasks (`tasks`) with a target
              Smartsheet sheet.
            </p>
          </div>
        </div>

        {pipes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center space-y-3">
            <p className="text-sm font-semibold text-slate-900">
              No synchronization pipes configured yet
            </p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Create a Schedule Tasks pipe to connect this Procore project with Smartsheet.
            </p>
            <button
              type="button"
              onClick={openCreatePipeModal}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              <IconPlus className="w-4 h-4" />
              <span>Create First Pipe</span>
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
            <div className="divide-y divide-slate-200">
              {pipesPagination.pageItems.map((pipe) => {
                const badge = pipeStatusBadge(pipe.status);
                const columnCount = Object.keys(pipe.smartsheet?.column_map || {}).length;

                return (
                  <article
                    key={pipe.id}
                    className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 hover:bg-slate-50/60 transition-colors"
                  >
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <h3 className="text-base font-bold text-slate-900">{pipe.name}</h3>
                        <Badge variant="purple">{pipe.pipe_type.toUpperCase()}</Badge>
                        <Badge variant="info">smartsheet</Badge>
                        <Badge variant={badge.variant} dot>
                          {badge.label}
                        </Badge>
                        {pipe.is_locked && (
                          <Badge variant="danger" dot>
                            Sync Lock Active
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-600 tabular-nums">
                        {pipe.smartsheet?.sheet_name && (
                          <span>
                            Target Sheet:{' '}
                            <strong className="text-slate-800">{pipe.smartsheet.sheet_name}</strong>{' '}
                            <code className="font-mono text-slate-500">
                              (#{pipe.smartsheet.sheet_id})
                            </code>
                          </span>
                        )}
                        <span>
                          Mapped Columns: <strong>{columnCount}</strong>
                        </span>
                        <span>
                          Last Sync:{' '}
                          <strong>{formatRelativeTime(pipe.last_sync_at)}</strong>
                        </span>
                        {pipe.settings?.use_schedule_chart && (
                          <span className="text-indigo-600 font-medium">
                            Gantt PDF ({pipe.settings.procore_pdf_size})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Pipe Actions Toolbar */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={busyPipeId === pipe.id}
                        onClick={() =>
                          handleTriggerDirectionalSync(pipe, 'procore_to_smartsheet')
                        }
                        className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        title="Run Procore → Smartsheet sync"
                      >
                        <IconSync className="w-3.5 h-3.5" />
                        <span>Procore → Target</span>
                      </button>

                      <button
                        type="button"
                        disabled={busyPipeId === pipe.id}
                        onClick={() =>
                          handleTriggerDirectionalSync(pipe, 'smartsheet_to_procore')
                        }
                        className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                        title="Run Smartsheet → Procore sync"
                      >
                        <IconSync className="w-3.5 h-3.5" />
                        <span>Target → Procore</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => openColumnMatching(pipe)}
                        className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 tabular-nums"
                      >
                        Column Matching ({columnCount})
                      </button>

                      <button
                        type="button"
                        onClick={() => openSyncHistory(pipe)}
                        className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Sync History &amp; Logs
                      </button>

                      <button
                        type="button"
                        onClick={() => openEditPipeSettings(pipe)}
                        className="rounded-xl border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        title="Pipe Settings & Diagnostics"
                      >
                        <IconSettings className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        disabled={busyPipeId === pipe.id}
                        onClick={() => handleTogglePipeEnabled(pipe)}
                        className="rounded-xl border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        {pipe.status === 'connected' ? 'Disable' : 'Enable'}
                      </button>

                      {pipe.is_locked && (
                        <button
                          type="button"
                          onClick={() => handleReleasePipeLock(pipe)}
                          className="rounded-xl border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                        >
                          Unlock
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => handleDeletePipe(pipe)}
                        className="rounded-xl border border-rose-200 bg-rose-50 p-1.5 text-rose-700 hover:bg-rose-100"
                        title="Delete Pipe"
                      >
                        <IconTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            <PaginationBar
              pagination={pipesPagination}
              itemLabel="pipes"
              className="rounded-b-xl"
            />
          </div>
        )}
      </section>

      {/* Dialog 1: Create Pipe Wizard */}
      <Dialog
        open={createPipeOpen}
        onClose={() => setCreatePipeOpen(false)}
        title="Create Synchronization Pipe"
        subtitle="Select a Procore module and target Smartsheet sheet."
        maxWidthClass="max-w-xl"
      >
        <form onSubmit={handleCreatePipe} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Pipe Name <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              required
              value={pipeName}
              onChange={(e) => setPipeName(e.target.value)}
              placeholder="Master Construction Schedule Sync"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Procore Data Module (`pipe_type`)
              </label>
              <select
                value={pipeType}
                onChange={(e) => setPipeType(e.target.value as PipeType)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium"
              >
                <option value="tasks">Schedule &amp; Tasks (`tasks`)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Target Platform (`connected_to`)
              </label>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800">
                <span>Smartsheet</span>
                <Badge variant="info">Active Integration</Badge>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center gap-4 text-xs font-semibold text-slate-700">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="sheetMode"
                  checked={sheetSelectionMode === 'existing'}
                  onChange={() => setSheetSelectionMode('existing')}
                />
                <span>Select Existing Smartsheet</span>
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="sheetMode"
                  checked={sheetSelectionMode === 'create'}
                  onChange={() => setSheetSelectionMode('create')}
                />
                <span>Create New Smartsheet</span>
              </label>
            </div>

            {sheetSelectionMode === 'existing' ? (
              <div>
                {loadingSheets ? (
                  <p className="text-xs text-slate-500">Loading Smartsheet sheets…</p>
                ) : availableSheets.length > 0 ? (
                  <select
                    value={selectedSheetId}
                    onChange={(e) => setSelectedSheetId(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
                  >
                    {availableSheets.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-xs text-slate-500">
                    No existing Smartsheet sheets found. Choose &ldquo;Create New Smartsheet&rdquo;
                    above or connect Smartsheet on the Connections page.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  New Smartsheet Name
                </label>
                <input
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder={`${project.name} - Schedule`}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
                />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <input
              type="checkbox"
              checked={useScheduleChart}
              onChange={(e) => setUseScheduleChart(e.target.checked)}
              className="rounded border-slate-300"
            />
            <span>Export &amp; attach Gantt schedule PDF chart to Procore Documents</span>
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setCreatePipeOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingPipe}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingPipe ? 'Creating Pipe…' : 'Create Pipe'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Dialog 2: Column Matching / Field Mapping */}
      <Dialog
        open={Boolean(mappingPipe)}
        onClose={() => setMappingPipe(null)}
        title={`Column Matching — ${mappingPipe?.name || ''}`}
        subtitle="Map Procore fields to target Smartsheet columns."
        maxWidthClass="max-w-2xl"
      >
        {mappingPipe && (
          <form onSubmit={handleSaveColumnMatching} className="space-y-4">
            {loadingColumns && (
              <p className="text-xs text-slate-500">
                Fetching sheet columns from Smartsheet…
              </p>
            )}

            <div className="rounded-xl border border-slate-200 divide-y divide-slate-200">
              {(STANDARD_FIELDS_BY_PIPE_TYPE[mappingPipe.pipe_type] || []).map((field) => (
                <div
                  key={field.key}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3"
                >
                  <div>
                    <span className="text-xs font-semibold text-slate-800">{field.label}</span>
                    {field.required && <span className="ml-1 text-rose-600">*</span>}
                    <div className="text-[11px] font-mono text-slate-400">{field.key}</div>
                  </div>

                  <div className="sm:w-64">
                    {remoteColumns.length > 0 ? (
                      <select
                        value={columnMapDraft[field.key] || ''}
                        onChange={(e) =>
                          setColumnMapDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
                      >
                        <option value="">-- Unmapped --</option>
                        {remoteColumns.map((col) => (
                          <option key={col.id} value={String(col.id)}>
                            {col.title} ({col.column_type} #{col.id})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={columnMapDraft[field.key] || ''}
                        onChange={(e) =>
                          setColumnMapDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={`Target column ID or title for ${field.key}`}
                        className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-mono"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  const autoFilled: Record<string, string> = {};
                  (STANDARD_FIELDS_BY_PIPE_TYPE[mappingPipe.pipe_type] || []).forEach(
                    (field, idx) => {
                      if (remoteColumns[idx]) {
                        autoFilled[field.key] = String(remoteColumns[idx].id);
                      } else {
                        autoFilled[field.key] = `col_${field.key}`;
                      }
                    },
                  );
                  setColumnMapDraft(autoFilled);
                }}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                Auto-Match Default Columns
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMappingPipe(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingColumns}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingColumns ? 'Saving Mapping…' : 'Save Column Mapping'}
                </button>
              </div>
            </div>
          </form>
        )}
      </Dialog>

      {/* Dialog 3: Pipe Settings, Gantt Validation & Duplicate Cleanup */}
      <Dialog
        open={Boolean(editingPipe)}
        onClose={() => setEditingPipe(null)}
        title={`Pipe Configuration & Diagnostics — ${editingPipe?.name || ''}`}
        subtitle="Manage schedule chart export options, color coding, summary tasks, and schedule health tools."
        maxWidthClass="max-w-xl"
      >
        {editingPipe && (
          <div className="space-y-6">
            <form onSubmit={handleSavePipeSettings} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Pipe Display Name
                </label>
                <input
                  type="text"
                  required
                  value={editPipeName}
                  onChange={(e) => setEditPipeName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div className="space-y-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                  <input
                    type="checkbox"
                    checked={editUseScheduleChart}
                    onChange={(e) => setEditUseScheduleChart(e.target.checked)}
                  />
                  <span>Enable Gantt Schedule PDF Chart export (`use_schedule_chart`)</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                  <input
                    type="checkbox"
                    checked={editColorsCoding}
                    onChange={(e) => setEditColorsCoding(e.target.checked)}
                  />
                  <span>Preserve Smartsheet row color coding (`colors_coding_enabled`)</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                  <input
                    type="checkbox"
                    checked={editSummaryTasks}
                    onChange={(e) => setEditSummaryTasks(e.target.checked)}
                  />
                  <span>Synchronize WBS summary / parent tasks (`summary_tasks_enabled`)</span>
                </label>

                <div className="pt-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">Procore PDF Page Size:</span>
                  <select
                    value={editPdfSize}
                    onChange={(e) => setEditPdfSize(e.target.value as ProcorePdfSize)}
                    className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-mono"
                  >
                    <option value="A0">A0</option>
                    <option value="A1">A1</option>
                    <option value="A2">A2</option>
                    <option value="A3">A3</option>
                    <option value="A4">A4</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="submit"
                  disabled={savingPipeSettings}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingPipeSettings ? 'Saving…' : 'Save Pipe Settings'}
                </button>
              </div>
            </form>

            {/* Diagnostics & Maintenance Tools */}
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Schedule Diagnostics &amp; Cleanup Tools
              </h4>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleRunGanttValidation}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Validate Gantt Task Graph
                </button>
                <button
                  type="button"
                  onClick={handleCleanupDuplicateTasks}
                  className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                >
                  Clean Up Duplicate Tasks
                </button>
              </div>

              {validationResult && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs">
                  <p className="font-semibold text-slate-900">
                    Validation Result: {validationResult.valid ? 'Valid DAG' : 'Issues Detected'} (
                    {validationResult.checked_tasks} tasks checked)
                  </p>
                  {validationResult.issues.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-rose-700">
                      {validationResult.issues.map((iss, idx) => (
                        <li key={idx}>
                          [{iss.code}] {iss.task_name}: {iss.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      {/* Dialog 4: Sync History & Item Change Logs */}
      <Dialog
        open={Boolean(historyPipe)}
        onClose={() => setHistoryPipe(null)}
        title={`Sync History & Item Change Logs — ${historyPipe?.name || ''}`}
        subtitle="Inspect sync sessions and field-level diffs."
        maxWidthClass="max-w-4xl"
      >
        {historyPipe && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {/* Left Column: Sync Sessions List */}
              <div className="lg:col-span-5 space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 tabular-nums">
                  Recent Sync Sessions ({sessions.length})
                </h4>
                {loadingSessions ? (
                  <p className="text-xs text-slate-500 py-4">Loading sync sessions…</p>
                ) : sessions.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                    No sync sessions recorded yet. Trigger a manual sync to generate a session.
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <ul className="divide-y divide-slate-200 max-h-80 overflow-y-auto">
                      {sessionsPagination.pageItems.map((sess) => {
                        const active = selectedSessionId === sess.id;
                        return (
                          <li key={sess.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectSyncSession(sess.id)}
                              className={`w-full text-left p-3 transition-colors ${
                                active ? 'bg-indigo-50/90' : 'hover:bg-slate-50'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <Badge
                                  variant={
                                    sess.state === 'completed'
                                      ? 'success'
                                      : sess.state === 'failed'
                                        ? 'danger'
                                        : 'info'
                                  }
                                >
                                  {sess.state}
                                </Badge>
                                <span className="text-[11px] text-slate-500 tabular-nums">
                                  {formatDateTime(sess.started_at)}
                                </span>
                              </div>
                              <div className="mt-1.5 text-xs font-medium text-slate-800">
                                {sess.direction.replace(/_/g, ' → ')} ({sess.trigger})
                              </div>
                              <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 tabular-nums">
                                <span>+{sess.metrics?.items_created || 0} created</span>
                                <span>~{sess.metrics?.items_updated || 0} updated</span>
                                <span>-{sess.metrics?.items_deleted || 0} deleted</span>
                                {sess.duration_ms !== null && <span>{sess.duration_ms}ms</span>}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <PaginationBar
                      pagination={sessionsPagination}
                      itemLabel="sessions"
                      compact
                    />
                  </div>
                )}
              </div>

              {/* Right Column: Item Changes for Selected Session */}
              <div className="lg:col-span-7 space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 tabular-nums">
                  Item Change Audit Log ({sessionChanges.length})
                </h4>
                {loadingChanges ? (
                  <p className="text-xs text-slate-500 py-4">Loading item changes…</p>
                ) : sessionChanges.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                    {selectedSessionId
                      ? 'No item-level changes recorded for this session.'
                      : 'Select a sync session on the left to inspect item changes.'}
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="max-h-80 overflow-y-auto divide-y divide-slate-200">
                      {changesPagination.pageItems.map((chg) => (
                        <div key={chg.id} className="p-3 space-y-1 text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-900">
                              {chg.item_name || chg.procore_item_id || chg.id}
                            </span>
                            <Badge
                              variant={
                                chg.action === 'created'
                                  ? 'success'
                                  : chg.action === 'updated'
                                    ? 'info'
                                    : chg.action === 'failed'
                                      ? 'danger'
                                      : 'neutral'
                              }
                            >
                              {chg.action}
                            </Badge>
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono tabular-nums">
                            Procore ID: {chg.procore_item_id || '—'} ↔ Target ID:{' '}
                            {chg.target_item_id || '—'}
                          </div>
                          {chg.error_message && (
                            <p className="text-rose-600 text-[11px]">{chg.error_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <PaginationBar
                      pagination={changesPagination}
                      itemLabel="changes"
                      compact
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Active Synced Item Mappings Summary */}
            <div className="border-t border-slate-200 pt-3 flex items-center justify-between text-xs text-slate-600">
              <span>
                Total Tracked Item Mappings:{' '}
                <strong>{syncedItems.length}</strong>
              </span>
              <button
                type="button"
                onClick={() => setHistoryPipe(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Dialog 5: Project Settings */}
      <Dialog
        open={projectSettingsOpen}
        onClose={() => setProjectSettingsOpen(false)}
        title={`Project Settings — ${project.name}`}
        subtitle="Configure working calendar days, holidays, notification policy, and invoicing."
      >
        <form onSubmit={handleSaveProjectSettings} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Working Days of the Week
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { day: 1, label: 'Mon' },
                { day: 2, label: 'Tue' },
                { day: 3, label: 'Wed' },
                { day: 4, label: 'Thu' },
                { day: 5, label: 'Fri' },
                { day: 6, label: 'Sat' },
                { day: 7, label: 'Sun' },
              ].map(({ day, label }) => {
                const checked = workingDaysDraft.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() =>
                      setWorkingDaysDraft((prev) =>
                        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
                      )
                    }
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                      checked
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-slate-300 bg-white text-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Non-Working Holidays (YYYY-MM-DD, comma-separated)
            </label>
            <input
              type="text"
              value={holidaysDraft}
              onChange={(e) => setHolidaysDraft(e.target.value)}
              placeholder="2026-07-04, 2026-11-26, 2026-12-25"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Sync Error Notification Policy
            </label>
            <select
              value={notificationPolicyDraft}
              onChange={(e) =>
                setNotificationPolicyDraft(e.target.value as ProjectNotificationPolicy)
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
            >
              <option value="send_once_per_day">Send Daily Digest (`send_once_per_day`)</option>
              <option value="send_every_error">Send on Every Error (`send_every_error`)</option>
              <option value="disable_notification">
                Disable Notifications (`disable_notification`)
              </option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Additional Notification CC Emails (comma-separated)
            </label>
            <input
              type="text"
              value={additionalEmailsDraft}
              onChange={(e) => setAdditionalEmailsDraft(e.target.value)}
              placeholder="pm@company.com, superintendent@company.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs"
            />
          </div>

          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
              <input
                type="checkbox"
                checked={allowNestedDraft}
                onChange={(e) => setAllowNestedDraft(e.target.checked)}
              />
              <span>Allow nested WBS task hierarchy (`allow_nested_tasks`)</span>
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
              <input
                type="checkbox"
                checked={updateTasksStatusDraft}
                onChange={(e) => setUpdateTasksStatusDraft(e.target.checked)}
              />
              <span>Automatically update task completion status (`update_tasks_status`)</span>
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
              <input
                type="checkbox"
                checked={separateInvoiceDraft}
                onChange={(e) => setSeparateInvoiceDraft(e.target.checked)}
              />
              <span>Bill this project on a separate invoice (`is_separate_invoice`)</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setProjectSettingsOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingProjectSettings}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingProjectSettings ? 'Saving…' : 'Save Project Settings'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
